import { useEffect, useState } from 'react'
import { KVPhoto } from 'types'

type PhotoUrlType = 'thumbnail' | 'fullSize' | 'sam2' | 'siglip'

export const usePhotoUrl = (
  photo: KVPhoto | null,
  type: PhotoUrlType
): {
  url: string | null
  isLoading: boolean
  error: string | null
} => {
  const [url, setUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!photo) {
      setUrl(null)
      setIsLoading(false)
      setError(null)
      return
    }

    let isMounted = true
    let fetchedUrl: string | null = null
    setIsLoading(true)
    setError(null)

    let getUrlPromise: Promise<string>
    switch (type) {
      case 'thumbnail':
        getUrlPromise = photo.getThumbnailUrl()
        break
      case 'fullSize':
        getUrlPromise = photo.getFullSizeUrl()
        break
      case 'sam2':
        getUrlPromise = photo.getSam2Url()
        break
      case 'siglip':
        getUrlPromise = photo.getSiglipUrl()
        break
      default:
        setError('Invalid photo URL type')
        setIsLoading(false)
        return
    }

    getUrlPromise
      .then((resultUrl) => {
        if (isMounted) {
          fetchedUrl = resultUrl
          setUrl(resultUrl)
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message || 'Failed to load photo URL')
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
      if (fetchedUrl) {
        URL.revokeObjectURL(fetchedUrl)
      }
    }
  }, [photo, type])

  useEffect(() => {
    return () => {
      if (url) {
        URL.revokeObjectURL(url)
      }
    }
  }, [url])

  return { url, isLoading, error }
}
